// Logger draft -> persisted-Workout serialization + save-time analysis.
// Pure (no React, no component state). Split out of workoutMapping.ts
// (Track R) along the direction seam: this file owns draft->payload,
// the single-set PR projection, outlier detection input, and the small
// bilateral helpers. Hydration (workout->draft) lives in
// workoutHydration.ts + planStarterHydration.ts. All re-export through
// workoutMapping.ts.
import {
  NewWorkout,
  WORKOUT_NAME_MAX,
  toCanonicalKg,
  type EffortScale,
  type NewWorkout as NewWorkoutT,
  type WorkoutSet as WorkoutSetT,
  parseLocaleNumber,
} from '@heraion/shared';
import { renderOrder } from './loggerUnits';
import type {
  DraftEntry,
  DraftSet,
  LoggerDraft,
} from './state/loggerDraftTypes';
import { savedRirOf } from './state/canonicalRir';
import {
  amrapField,
  sideField,
  buildDropsetSet,
  measuredDurationField,
  weightSourceField,
  type SetCommon,
} from './buildDropsetSet';

// Single-set DraftSet → WorkoutSet projection used by the click-time
// isSetPR check on the pill. Same parse/conversion semantics as the
// inner loop of buildPayload (which produces the save payload across
// all sets) — we keep the two in sync so a "PR at tap" never
// disagrees with what /api/me/records will reflect after the save
// lands. Returns null when reps/weight aren't yet typeable (empty,
// non-finite, negative); those rows can't be PR-eligible so the
// caller treats null as "no flash".
/**
 * Every COMPLETED set of one exercise in the open session, paired with the
 * entry it belongs to.
 *
 * Extracted 2026-08-13 when EQ-1's live load signature became the second
 * caller of a walk `priorSessionSetsForPr` already owned: entries → keep this
 * exercise → skip pending → (build). Two copies of "what has actually been
 * logged today" is the kind that drifts quietly — one would start counting a
 * pending row, or a superset's second entry, where the other stopped.
 *
 * Scans ALL entries, not just the first match: the same lift can be logged
 * twice in a session, and inside a superset it is several entries.
 *
 * Yields the ENTRY too, because the callers need different things from it —
 * the PR check is told which bodyweight mode to use, the signature reads the
 * entry's own machine. Building the WorkoutSet is left to them for the same
 * reason.
 */
export const completedDraftSetsFor = (
  entries: readonly DraftEntry[],
  exerciseId: string,
): { entry: DraftEntry; set: DraftSet }[] => {
  const out: { entry: DraftEntry; set: DraftSet }[] = [];
  for (const entry of entries) {
    if (entry.exerciseId !== exerciseId) continue;
    for (const set of entry.sets) {
      // A pending row holds whatever is half-typed into it.
      if (set.completedAt === null) continue;
      out.push({ entry, set });
    }
  }
  return out;
};

export const buildWorkoutSetFromDraft = (
  s: DraftSet,
  isBodyweight: boolean,
): WorkoutSetT | null => {
  const reps = parseInt(s.reps, 10);
  if (!Number.isFinite(reps) || reps < 0) return null;
  if (isBodyweight) {
    const extraRaw = s.weight.trim();
    if (extraRaw === '') return { type: 'bodyweight', reps };
    const extraAbs = parseLocaleNumber(extraRaw);
    if (!Number.isFinite(extraAbs)) return null;
    const signed = s.extraNegative ? -Math.abs(extraAbs) : extraAbs;
    return {
      type: 'bodyweight',
      reps,
      extra: toCanonicalKg(signed, s.unit),
    };
  }
  const weight = parseLocaleNumber(s.weight);
  if (!Number.isFinite(weight) || weight < 0) return null;
  return {
    type: 'weighted',
    reps,
    weight: toCanonicalKg(weight, s.unit),
  };
};

// Outlier weight detection at save time moved to detectOutliersForDraft.ts
// (AP-0p, LG-8) to keep both files under the 500-line cap. Re-exported
// through workoutMapping.ts so useSaveWorkout's import is unchanged.

export const buildPayload = (
  draft: LoggerDraft,
  // Source-of-truth scale for the single effort column. Defaults to the
  // Profile default so callers that don't track effort (and tests) behave
  // as before; the live save path passes the athlete's real preference.
  effortScale: EffortScale = 'rir',
  // Timeline logger (Phase 2): drop any set the athlete never marked done
  // (completedAt === null). The timeline FocusPanel ALWAYS shows a "next
  // set" input that the user may never LOG — Antenore's rule (2026-05-30):
  // "in fase di salvataggio, se c'è un set che non ho marcato come fatto,
  // non lo deve contare", matching design §7.3 (not-done ≠ a saved set; an
  // untouched proposed set is dropped). The CARD logger omits this →
  // unchanged: it keeps saving any set with finite reps regardless of the
  // pill state, as the family's flow relies on.
  opts: { dropUncompleted?: boolean; fallbackName?: string } = {},
): { ok: true; payload: NewWorkoutT } | { ok: false; reason: string } => {
  if (draft.entries.length === 0) {
    return { ok: false, reason: 'mobile.logger.errNoEntries' };
  }
  const entries: NewWorkoutT['entries'] = [];
  // ⛔ renderOrder, NOT draft.entries. The raw array can hold a group whose
  // members are not contiguous (any draft hydrated from a session saved before
  // PE-20), and walking it would save an order the athlete never saw. One
  // order, and it is the drawn one. See loggerUnits.renderOrder.
  for (const e of renderOrder(draft.entries)) {
    const builtSets: WorkoutSetT[] = [];
    for (const s of e.sets) {
      // Timeline mode: a set never marked done is not a logged set — skip it
      // entirely (whatever it holds), before any field parsing. An entry left
      // with zero built sets is then dropped by the length===0 guard below.
      if (opts.dropUncompleted && s.completedAt === null) continue;
      const noteTrim = (s.note ?? '').trim();
      const common: SetCommon = {};
      if (s.completedAt !== null) common.completedAt = s.completedAt;
      if (s.fail) common.fail = true;
      if (noteTrim) common.note = noteTrim.slice(0, 500);
      // Single effort column: ONE canonical RIR is saved (AP-0a, Decision 8:
      // "mai due campi"), resolved by savedRirOf from the athlete's scale.
      const canonicalRir = savedRirOf(s.rir, s.rpe, effortScale);
      if (canonicalRir !== null) common.rir = canonicalRir;
      if (e.isCardio) {
        // Cardio sets (1.66.9 Logger UI): duration is the only
        // required field; the four optional ones each pass the schema
        // shape check before being included. Drop the set entirely if
        // the user typed no duration — the schema's .min(1) seconds
        // floor would reject it anyway and silently emitting zeros
        // would pollute history.
        const duration = parseInt(s.duration, 10);
        if (!Number.isFinite(duration) || duration < 1) continue;
        const distance = parseLocaleNumber(s.distanceM);
        const hr = parseInt(s.avgHrBpm, 10);
        const kcal = parseLocaleNumber(s.calories);
        const weight = parseLocaleNumber(s.weight);
        builtSets.push({
          type: 'cardio',
          duration,
          ...(Number.isFinite(distance) && distance > 0
            ? { distanceM: distance }
            : {}),
          ...(Number.isFinite(weight) && weight > 0
            ? { weight: toCanonicalKg(weight, s.unit) }
            : {}),
          ...(Number.isFinite(hr) && hr >= 30 && hr <= 250
            ? { avgHrBpm: hr }
            : {}),
          ...(Number.isFinite(kcal) && kcal > 0 ? { calories: kcal } : {}),
          ...common,
        });
        continue;
      }
      if (e.isTimeBased) {
        // Isometric hold (inbox 3dd20b1c): seconds held is the required
        // metric; weight is an optional added load (weighted plank/hold).
        // Same drop-if-no-duration rule as cardio — the schema's .min(1)
        // floor rejects a zero hold and emitting it would pollute history.
        const duration = parseInt(s.duration, 10);
        if (!Number.isFinite(duration) || duration < 1) continue;
        const holdWeight = parseLocaleNumber(s.weight);
        builtSets.push({
          type: 'timed',
          duration,
          ...(Number.isFinite(holdWeight) && holdWeight > 0
            ? { weight: toCanonicalKg(holdWeight, s.unit) }
            : {}),
          ...common,
        });
        continue;
      }
      // Dropset / myorep: the whole chain (top + drops) is assembled in
      // buildDropsetSet, which derives the parent set from the TOP round
      // — so an empty parent rep count (the chain carries the data) no
      // longer drops the set. Plain sets fall through to the reps gate
      // below.
      if (s.dropset !== null) {
        const built = buildDropsetSet(s, e.isBodyweight, common);
        if (built) builtSets.push(built);
        continue;
      }
      const reps = parseInt(s.reps, 10);
      if (!Number.isFinite(reps) || reps < 0) continue;
      if (e.isBodyweight) {
        // Bodyweight: extra is optional. Empty = pure BW. Non-empty
        // numeric = added load (positive) or assistance (negative).
        // Android's decimal-pad has no minus sign, so the per-set
        // sign is captured via `extraNegative`; we apply it here.
        const extraRaw = s.weight.trim();
        // Laterality travels EXPLICITLY on every set since Slice 2 of
        // IB-8 (2026-07-14): the server stamps the catalog default only
        // when the field is ABSENT, so an explicit 1 is how a deliberate
        // BI toggle on a unilateral lift survives the server-side guard.
        const bwSides = { loadedSides: s.loadedSides };
        if (extraRaw === '') {
          builtSets.push({
            type: 'bodyweight',
            reps,
            ...bwSides,
            ...common,
            ...measuredDurationField(s),
            ...weightSourceField(s),
            ...amrapField(s),
            ...sideField(s),
          });
        } else {
          const extraAbs = parseLocaleNumber(extraRaw);
          if (!Number.isFinite(extraAbs)) continue;
          const signed = s.extraNegative ? -Math.abs(extraAbs) : extraAbs;
          builtSets.push({
            type: 'bodyweight',
            reps,
            extra: toCanonicalKg(signed, s.unit),
            ...bwSides,
            ...common,
            ...measuredDurationField(s),
            ...weightSourceField(s),
            ...amrapField(s),
            ...sideField(s),
          });
        }
      } else {
        const weight = parseLocaleNumber(s.weight);
        if (!Number.isFinite(weight) || weight < 0) continue;
        builtSets.push({
          type: 'weighted',
          reps,
          weight: toCanonicalKg(weight, s.unit),
          // Explicit on every set (Slice 2, IB-8 2026-07-14): the old
          // lean-payload rule (omit 1) made "client chose bilateral"
          // indistinguishable from "client forgot the field", which is
          // what let the swap bug corrupt silently. The server now
          // stamps the catalog default ONLY when the field is absent;
          // an explicit 1 is the deliberate-BI signal.
          loadedSides: s.loadedSides,
          ...measuredDurationField(s),
          ...common,
          ...weightSourceField(s),
          ...amrapField(s),
          ...sideField(s),
          // #353: what lets the post-save MAX proposal read the ramp.
          ...(s.calibration === true ? { calibration: true } : {}),
        });
      }
    }
    if (builtSets.length === 0) continue;
    // Per-exercise note retired: it's display-only, resolved per-slug by usePlanCues.
    entries.push({
      exerciseId: e.exerciseId,
      sets: builtSets,
      // EQ-1: which machine, and where. Omitted when unset rather than
      // sent as null — the schema field is optional, and every workout
      // already in the family's history looks exactly like that.
      ...(e.equipmentInstance ? { equipmentInstance: e.equipmentInstance } : {}),
      ...(e.location ? { location: e.location } : {}),
      // A correction writes back the snapshot it read (#38).
      ...(e.tags?.length ? { tags: e.tags } : {}),
      ...(e.group ? { group: e.group } : {}),
    });
  }
  if (entries.length === 0) {
    return { ok: false, reason: 'mobile.logger.errNoSets' };
  }
  // Group sanity: every superset/circuit needs at least 2 members. The
  // server stores the field as-is, so any half-built group reaches
  // history without context — block it client-side instead. Solo
  // entries get their group dropped silently (a one-member superset
  // is just a regular entry).
  const groupCounts = new Map<string, number>();
  for (const e of entries) {
    if (e.group) {
      groupCounts.set(e.group.id, (groupCounts.get(e.group.id) ?? 0) + 1);
    }
  }
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i];
    if (!e || !e.group) continue;
    if ((groupCounts.get(e.group.id) ?? 0) < 2) {
      const rest = { ...e };
      delete rest.group;
      entries[i] = rest;
    }
  }
  // endedAt resolution (timeline fix 2026-05-27, next/timeline.md root
  // cause 3): take the LATER of the user-typed end (footer date editor)
  // and the latest per-set completedAt. Letting draft.endedAt win
  // unconditionally left the stored end BEFORE sets added later during a
  // past-session edit, so the session "ended" before a third of its own
  // sets happened. The latest-completedAt fallback still omits endedAt
  // entirely when no set was completed, so abandoned sessions get no
  // synthetic duration (inbox d2467bc3).
  let latestCompletedAtMs = 0;
  for (const built of entries) {
    for (const s of built.sets) {
      if (s.completedAt) {
        const ms = new Date(s.completedAt).getTime();
        if (Number.isFinite(ms) && ms > latestCompletedAtMs) {
          latestCompletedAtMs = ms;
        }
      }
    }
  }
  const typedEndedMs = draft.endedAt ? Date.parse(draft.endedAt) : NaN;
  let resolvedEndedAt: string | undefined;
  if (Number.isFinite(typedEndedMs) && latestCompletedAtMs > 0) {
    resolvedEndedAt = new Date(
      Math.max(typedEndedMs, latestCompletedAtMs),
    ).toISOString();
  } else if (Number.isFinite(typedEndedMs)) {
    resolvedEndedAt = draft.endedAt;
  } else if (latestCompletedAtMs > 0) {
    resolvedEndedAt = new Date(latestCompletedAtMs).toISOString();
  }
  const payload: NewWorkoutT = {
    startedAt: draft.startedAt,
    ...(resolvedEndedAt ? { endedAt: resolvedEndedAt } : {}),
    entries,
    ...(draft.fromPlanId ? { fromPlanId: draft.fromPlanId } : {}),
    // Name: the user's typed name wins; otherwise fall back to the live
    // auto-suggestion so a blank workout still lands in history with a
    // meaningful title (never a stored snapshot until this save).
    ...(() => {
      const resolved = draft.name?.trim() || opts.fallbackName?.trim();
      return resolved ? { name: resolved.slice(0, WORKOUT_NAME_MAX) } : {};
    })(),
    ...(draft.notes ? { instructions: draft.notes.slice(0, 1000) } : {}),
  };
  const parsed = NewWorkout.safeParse(payload);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const message = first?.message ?? '';
    return {
      ok: false,
      reason:
        path || message
          ? `${path}${path && message ? ': ' : ''}${message}`
          : 'mobile.logger.errInvalid',
    };
  }
  return { ok: true, payload: parsed.data };
};
